import { useMemo } from 'react';
import { NotificationChannel } from '../../gql/generated';
import {
  SidePanel,
  SidePanelTab,
  SidePanelTabContent,
  SidePanelTabList,
  SidePanelTabs,
} from '../SidePanel';
import { ChannelDetailsTab } from './ChannelDetailsTab';
import { ChannelTestResultsTab } from './ChannelTestResultsTab';

const channelTabOrder = ['information', 'test-results'];

export interface ChannelTestResultEntry {
  id: string;
  channelId: string;
  status: 'success' | 'error';
  message: string;
  timestamp: string;
}

interface ChannelSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: NotificationChannel | null;
  availableChannels: NotificationChannel[];
  testResults: ChannelTestResultEntry[];
  onSendTestNotification: (channelId: string) => Promise<void>;
  isSendingTestNotification: boolean;
}

export function ChannelSidePanel({
  open,
  onOpenChange,
  channel,
  availableChannels,
  testResults,
  onSendTestNotification,
  isSendingTestNotification,
}: ChannelSidePanelProps) {
  const groupedChannelNames = useMemo(() => {
    if (!channel?.groupedChannelIds?.length) return [];

    const nameById = new Map(availableChannels.map((item) => [item.id, item.name]));

    return channel.groupedChannelIds.map(
      (groupedId) => nameById.get(groupedId) || groupedId
    );
  }, [availableChannels, channel]);

  if (!channel || !open) return null;

  const subtitle = `${channel.channelType} - ${channel.isEnabled ? 'Enabled' : 'Disabled'}`;

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={channel.name}
      subtitle={subtitle}
    >
      <SidePanelTabs
        defaultValue='information'
        className='flex h-full flex-col'
        enableSwipe
        tabOrder={channelTabOrder}
      >
        <SidePanelTabList>
          <SidePanelTab value='information'>Channel information</SidePanelTab>
          <SidePanelTab value='test-results'>Test results</SidePanelTab>
        </SidePanelTabList>

        <SidePanelTabContent
          value='information'
          className='flex-1 overflow-y-auto'
        >
          <ChannelDetailsTab channel={channel} groupedChannelNames={groupedChannelNames} />
        </SidePanelTabContent>

        <SidePanelTabContent
          value='test-results'
          className='flex-1 overflow-y-auto'
        >
          <ChannelTestResultsTab
            testResults={testResults}
            onSendTestNotification={() => onSendTestNotification(channel.id)}
            isSendingTestNotification={isSendingTestNotification}
          />
        </SidePanelTabContent>
      </SidePanelTabs>
    </SidePanel>
  );
}
